import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, Search as SearchIcon, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { RecommendationCard } from '../components/RecommendationCard';
import { CategoryChips } from '../components/CategoryChips';
import { PersonPicker } from '../components/PersonPicker';
import { usePeople } from '../lib/usePeople';
import { toast } from 'sonner';

export const Saved = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const people = usePeople(user, isAuthenticated && !authLoading);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setItems(await api.getMySaves());
    } catch (err) {
      toast.error('Could not load your saved recommendations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    load();
  }, [authLoading, isAuthenticated, load]);

  // Client-side filtering of the already privacy-filtered Saved result.
  const term = query.trim().toLowerCase();
  const visible = items.filter((rec) => {
    if (selectedPeople.length && !selectedPeople.includes(rec.authorId)) return false;
    if (category && rec.category !== category) return false;
    if (!term) return true;
    return `${rec.name} ${rec.reason} ${rec.authorName}`.toLowerCase().includes(term);
  });

  const handleSaveChange = (recId, isSaved) => {
    if (!isSaved) setItems((prev) => prev.filter((r) => r.id !== recId));
  };

  const clearFilters = () => {
    setQuery('');
    setCategory('');
    setSelectedPeople([]);
  };

  if (authLoading || loading) {
    return (
      <div className="page" data-testid="saved-page">
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="page" data-testid="saved-page">
        <header className="page-header">
          <h1 className="page-title">Saved</h1>
        </header>
        <div className="placeholder">
          <Bookmark className="placeholder-icon" />
          <h2 className="placeholder-title">Save recommendations</h2>
          <p className="placeholder-text">Sign in to keep the recommendations you want to remember.</p>
          <button className="placeholder-btn" onClick={() => navigate('/signin', { state: { from: '/saved' } })}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="saved-page">
      <header className="page-header">
        <h1 className="page-title">Saved</h1>
        <p className="page-subtitle">Recommendations you want to remember.</p>
      </header>

      {items.length > 0 && (
        <div className="filter-bar">
          <div className="search-field">
            <SearchIcon className="search-field-icon" />
            <input
              type="text"
              className="search-field-input"
              placeholder="Search saved recommendations…"
              aria-label="Search saved recommendations"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="saved-search"
            />
            {query && (
              <button
                className="search-field-clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                data-testid="saved-search-clear"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <CategoryChips value={category} onChange={setCategory} idPrefix="saved" />
          <PersonPicker people={people} value={selectedPeople} onChange={setSelectedPeople} idPrefix="saved" />
        </div>
      )}

      {visible.length > 0 ? (
        <div className="feed-list" data-testid="saved-list">
          {visible.map((rec) => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onSaveChange={handleSaveChange}
              allowOwnerUnsave
            />
          ))}
        </div>
      ) : items.length > 0 ? (
        <div className="placeholder" data-testid="saved-no-results">
          <SearchIcon className="placeholder-icon" />
          <h2 className="placeholder-title">No saved recommendations found</h2>
          <p className="placeholder-text">
            Try a different word, another category, or all people.
          </p>
          <button className="placeholder-btn" onClick={clearFilters} data-testid="saved-clear-filters">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="placeholder" data-testid="saved-empty">
          <Bookmark className="placeholder-icon" />
          <h2 className="placeholder-title">Nothing saved yet</h2>
          <p className="placeholder-text">
            When someone you trust recommends something you want to remember, save it here for later.
          </p>
          <button className="placeholder-btn" onClick={() => navigate('/')} data-testid="saved-browse">
            Browse recommendations
          </button>
        </div>
      )}
    </div>
  );
};
